import { createAsyncThunk } from '@reduxjs/toolkit';
import { errMsg } from '../utils/errMsg';
import {
    setFollowedLabels, addLabelFollow, removeLabelFollow, updateLabelFollowedSet, updateLabelNotifySet,
    setLabelsLoading, setLabelsError,
} from '../slices/followedLabelsSlice';
import { refreshAndRetry } from '../utils/refreshAndRetry';
import { asCatalogId } from './likesThunk';
import { track } from '../../utils/analytics';
import { emitNotifyToast } from '../../utils/notifyToast';
import { FOLLOW_TOAST_EVENT } from './followedArtistsThunk';

// Followed labels: followedArtistsThunk's twin. A label is identified by its
// catalog public id (never a name, never an int).
function emitFollowToast(detail) {
    if (typeof window === 'undefined') return;
    try { window.dispatchEvent(new CustomEvent(FOLLOW_TOAST_EVENT, { detail: { ...detail, entityType: 'label' } })); } catch { /* non-DOM env */ }
}

export const fetchFollowedLabels = createAsyncThunk(
    'followedLabels/fetch',
    async ({ page = 1, limit = 100 } = {}, { dispatch }) => {
        if (!window.electron?.library?.getFollowedLabels) return;
        try {
            dispatch(setLabelsLoading(true));
            const result = await refreshAndRetry((token) => window.electron.library.getFollowedLabels(token, page, limit));
            if (result.error) { dispatch(setLabelsError(errMsg(result.error))); return; }
            const data = result.data || [];
            dispatch(setFollowedLabels({ data, total: result.pagination?.total || data.length }));
            const followed = {}; const notify = {};
            for (const l of data) { followed[l.label_public_id] = true; notify[l.label_public_id] = !!l.notify; }
            dispatch(updateLabelFollowedSet(followed));
            dispatch(updateLabelNotifySet(notify));
        } catch (error) {
            dispatch(setLabelsError(errMsg(error)));
        } finally {
            dispatch(setLabelsLoading(false));
        }
    }
);

// label: { publicId, name, artworkUrl?, from?, quiet? } → true when saved
export const followLabel = createAsyncThunk(
    'followedLabels/follow',
    async (label, { dispatch }) => {
        const labelPublicId = asCatalogId(label.publicId || label.label_public_id);
        const name = label.name || label.label_name || '';
        if (!labelPublicId || !name) return false;
        const artwork = label.artworkUrl || label.artwork || null;
        dispatch(addLabelFollow({ label_public_id: labelPublicId, label_name: name, artwork, notify: false, created_at: new Date().toISOString() }));
        emitFollowToast({ status: 'followed', artistName: name, artwork, from: label.from || null, quiet: !!label.quiet });
        try {
            const result = await refreshAndRetry((token) => window.electron.library.followLabel(token, { labelPublicId, labelName: name, artwork }));
            if (result.error) throw new Error(errMsg(result.error));
            track('label_followed', { label_id: labelPublicId, label_name: name, from: label.from || 'button' });
            return true;
        } catch (error) {
            dispatch(removeLabelFollow({ labelPublicId }));
            emitFollowToast({ status: 'failed', artistName: name });
            console.error('Error following label:', error);
            return false;
        }
    }
);

export const unfollowLabel = createAsyncThunk(
    'followedLabels/unfollow',
    async ({ labelPublicId, name, artwork, from, quiet }, { dispatch, getState }) => {
        const fl = getState().followedLabels || {};
        const row = (fl.labels || []).find((l) => l.label_public_id === labelPublicId) || null;
        const wasNotify = !!(fl.notifySet || {})[labelPublicId];
        const labelName = name || row?.label_name || '';
        const art = row?.artwork || artwork || null;
        dispatch(removeLabelFollow({ labelPublicId }));
        emitFollowToast({
            status: 'unfollowed', artistName: labelName, artwork: art, from: from || null, quiet: !!quiet, wasNotify,
            undoLabel: { publicId: labelPublicId, name: labelName, artworkUrl: art },
        });
        try {
            const result = await refreshAndRetry((token) => window.electron.library.unfollowLabel(token, labelPublicId));
            if (result.error) console.error('Unfollow label failed:', result.error);
            else track('label_unfollowed', { label_id: labelPublicId, label_name: labelName, from: from || undefined });
        } catch (error) {
            console.error('Error unfollowing label:', error);
        }
    }
);

export const checkFollowedLabels = createAsyncThunk(
    'followedLabels/check',
    async (labelPublicIds, { dispatch }) => {
        const ids = (labelPublicIds || []).map(asCatalogId).filter(Boolean);
        if (!ids.length || !window.electron?.library?.checkFollowedLabels) return;
        try {
            const result = await refreshAndRetry((token) => window.electron.library.checkFollowedLabels(token, ids));
            if (result.error) return;
            const data = result.data || [];
            const followed = {}; const notify = {};
            ids.forEach((id, i) => { followed[id] = !!data[i]?.followed; notify[id] = !!data[i]?.notify; });
            dispatch(updateLabelFollowedSet(followed));
            dispatch(updateLabelNotifySet(notify));
        } catch (error) {
            console.error('Error checking followed labels:', error);
        }
    }
);

export const setLabelNotify = createAsyncThunk(
    'followedLabels/setNotify',
    async ({ labelPublicId, name, notify, from, artwork, quiet }, { dispatch, getState }) => {
        const row = (getState().followedLabels?.labels || []).find((l) => l.label_public_id === labelPublicId);
        const toast = { entityType: 'label', labelPublicId, name: name || row?.label_name || 'Label', artwork: artwork || row?.artwork || null, notify, quiet: !!quiet };
        dispatch(updateLabelNotifySet({ [labelPublicId]: notify }));
        emitNotifyToast({ status: 'ok', ...toast });
        try {
            const result = await refreshAndRetry((token) => window.electron.library.setLabelNotify(token, labelPublicId, notify));
            if (result.error) throw new Error(errMsg(result.error));
            track('label_notify_toggled', { label_id: labelPublicId, notify, from: from || 'button' });
        } catch (error) {
            dispatch(updateLabelNotifySet({ [labelPublicId]: !notify }));
            emitNotifyToast({ status: 'failed', ...toast });
            console.error('Error setting label notify:', error);
        }
    }
);
